import React, { Fragment } from 'react'
import styled from 'styled-components'
import { graphql } from 'react-apollo'
import PropTypes from 'prop-types'
import Cell from './Cell'
import processInput from './helpers/processInput'
import CreateCashflowDatum from './queries/CreateCashflowDatum'

const Container = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(5, 1fr);
  flex-direction: row;
  align-items: center;
  padding: 10px 10px 10px 0px;
  ${props => props.isEditable ? 'background: white;' : ''}
`
const Link = styled.a`
  text-align: right;
  cursor: pointer;
`
const SelectContainer = styled.div`
  width: 98.5%;
  text-align: right;
  justify-self: end;
  display: flex;
  flex-direction: column;
`
const Select = styled.select`
  width: 100%;
`
const Reason = styled.textarea`
  grid-column-start: 2;
  grid-column-end: 6;
  width: 93.5%;
  padding: 4px 6px;
  justify-self: end;
  align-self: end;
  resize: none;
  margin: 10px 0px 0px 0px;
`
const Button = styled.button`
  color: white;
  background: #dd6a01;
  padding: 2px 0px;
  width: 75%;
  border-radius: 3px;
  border: none;
  &:disabled {
    background: #dddddd;
    cursor: default;
  }
`
const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  grid-column-start: 6;
  grid-column-end: 6;
  text-align: center;
  margin-top: 8px;
`
const Cancel = Link.extend`
  width: 75%;
  text-align: left;
`
class Adjustment extends React.Component {
  constructor() {
    super()
    this.state = {
      isEditable: false,
      selection: null,
      isButtonDisabled: true
    }
    this.data = Array(5)
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevState.isEditable !== this.state.isEditable) {
      this.data = Array(5)
    }
  }
  _createValue = async (data, year) => {
    const { entityId, resourceType, years, createCashflowDatum } = this.props
    const { period } = years.get(year)
    const category = this.state.selection
    const reason = this.reason.value
    return await createCashflowDatum({
      variables: {
        input: {
          cashflowDatumFields: {
            year,
            resourceId: entityId,
            resourceType,
            category,
            data,
            reason,
            source: 'keyed'
          }
        }
      }
    })
  }
  enableEdit = () => {
    this.setState({
      isEditable: true
    })
  }
  disableEdit = () => {
    this.setState({
      isEditable: false
    })
  }
  validate = () => {
    const { selection } = this.state
    const reason = this.reason.value
    const isDataValid = this.data.some(d => typeof d === 'number')
    const isReasonValid = reason.length > 0
    const isSelectionValid = selection && selection !== '0'
    // Use this to prevent saving with negative values
    const areValuesNegative = this.data.some(d => d < 0)
    let isButtonDisabled = true
    if (isDataValid && isReasonValid && isSelectionValid && !areValuesNegative) {
      isButtonDisabled = false
    }
    this.setState({ isButtonDisabled })
  }
  onCategoryChange = e => {
    this.setState({
      selection: e.target.value
    }, this.validate)
  }
  onReasonChange = () => {
    this.validate()
  }
  renderCategories = () => {
    return this.props.availableCategories.map(category => {
      return <option key={category.key} value={category.key}>{category.title}</option>
    })
  }
  onKeyDown = e => {
    // Only numbers, spaces, tab, enter, left, right, backspace, delete, and plus sign are allowed
    const allowed = {
      8: true,
      9: true,
      13: true,
      37: true,
      39: true,
      46: true,
      48: true,
      49: true,
      50: true,
      51: true,
      52: true,
      53: true,
      54: true,
      55: true,
      56: true,
      57: true,
      67:  true,
      86:  true,
      96:  true,
      97:  true,
      98:  true,
      99:  true,
      100: true,
      101: true,
      102: true,
      103: true,
      104: true,
      105: true,
      187: true,
      189: true
    }
    if (e.keyCode === 187 && !e.shiftKey) {
      e.preventDefault()
    } else if (!(e.keyCode in allowed)) {
      e.preventDefault()
    }
  }
  onCellChange = (e, i) => {
    this.data[i] = processInput(e.target.value)
    this.validate()
  }
  renderSelect = () => {
    const { isEditable } = this.state
    const { segmentKey } = this.props
    if (isEditable) {
      return (
        <SelectContainer>
          <Select id={`${segmentKey}_add_category_select`} ref={node => this.select = node} defaultValue={0}
            onChange={this.onCategoryChange}
          >
            <option value="0">Please Select a Category</option>
            {this.renderCategories()}
          </Select>
        </SelectContainer>
      )
    } else {
      return (
        <Link onClick={this.enableEdit} id={`${segmentKey}_add_category`}>
          Add Additional Category
        </Link>
      )
    }
  }
  renderCells = () => {
    const { years, segmentKey } = this.props
    return Array.from(years.keys()).map((y, i) => {
      return (
        <Cell
          key={`${y}-adjustment-cell`}
          // onKeyDown={this.onKeyDown}
          onChange={e => this.onCellChange(e, i)}
          onBlur={this.onBlur}
          isEditable
          id={`${segmentKey}_add_category_cell`}
        />
      )
    })
  }
  save = () => {
    const { refetch, years } = this.props
    const y = Array.from(years.keys())
    const mutations = this.data.map((data, i) => {
      return this._createValue(data, y[i])
    })
    Promise.all(mutations).then(() => {
      this.disableEdit()
      this.setState({
        isButtonDisabled: true
      })
      refetch()
    }).catch(err => {
      console.log(err)
    })
  }
  onBlur = e => {
    e.target.value = processInput(e.target.value)
  }
  renderEditableElements = () => {
    const { isEditable, isButtonDisabled } = this.state
    const { segmentKey } = this.props
    const placeholder = 'Provide a reason here why the amount needs to be changed and we will create the adjustment once it\'s saved.'
    if (isEditable) {
      return (
        <Fragment>
          {this.renderCells()}
          <Reason id={`${segmentKey}_add_category_reason`} innerRef={node => this.reason = node} placeholder={placeholder}
            onChange={this.onReasonChange}
          />
          <Controls>
            <Button id={`${segmentKey}_add_category_save`} disabled={isButtonDisabled} onClick={this.save}>Save</Button>
            <Cancel id={`${segmentKey}_add_category_cancel`} onClick={this.disableEdit}>Cancel</Cancel>
          </Controls>
        </Fragment>
      )
    }
  }
  render() {
    const { segmentKey } = this.props
    return (
      <Container id={`${segmentKey}_add_category`} isEditable={this.props.isEditable}>
        {this.renderSelect()}
        {this.renderEditableElements()}
      </Container>
    )
  }
}

Adjustment.propTypes = {
  availableCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  entityId: PropTypes.number.isRequired,
  refetch: PropTypes.func.isRequired,
  years: PropTypes.object.isRequired
}

export default graphql(CreateCashflowDatum, {
  name: 'createCashflowDatum'
})(Adjustment)
